import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../app/store'
import { useGetProfileQuery } from '../features/profile/profileAPI'
import { ProfileData, CSProfileData, ShiftReport } from '../types'
import { isCustomerServiceUser } from '../utils/apiUtils'

interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

interface UseProfileReturn {
  // State
  profileData: ProfileData | CSProfileData | undefined
  isLoading: boolean
  error: any
  
  // Derived values
  isCS: boolean
  
  // Date filter
  selectedDateRange: DateRange | null
  applyDateFilter: (startDate: Date, endDate: Date | null) => void
  clearDateFilter: () => void
  
  // Formatting functions
  formatNumber: (number: number | string | undefined | null) => string
  formatShiftDate: (dateString: string) => string
  
  // Data getters
  employeeName: string
  totalSales: string
  totalOrders: number
  totalProducts: number
  cashSales: string
  cashOrdersCount: number
  networkSales: string
  networkOrdersCount: number
  partialSales: string
  partialOrdersCount: number
  bestSellingProducts: any[]
  shifts: ShiftReport[]
  getRecentShifts: () => ShiftReport[]
  
  // Actions
  refreshProfile: () => void
}

export function useProfile(): UseProfileReturn {
  const { t } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null)
  
  // Check if user is Customer Service
  const isCS = useMemo(() => isCustomerServiceUser(), [])
  
  // Build query parameters for date filter
  const queryParams = useMemo(() => {
    if (selectedDateRange?.startDate) {
      const startDate = selectedDateRange.startDate
      const endDate = selectedDateRange.endDate || startDate
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      
      const startDateStr = formatDate(startDate)
      const endDateStr = formatDate(endDate)
      
      // If same day, only send start_date
      if (startDateStr === endDateStr) {
        return { startDate: startDateStr }
      }
      
      return { startDate: startDateStr, endDate: endDateStr }
    }
    return undefined
  }, [selectedDateRange])
  
  // Fetch profile data
  const { data: profileData, isLoading, error, refetch } = useGetProfileQuery(queryParams)
  
  // Format number with thousands separators (matching Flutter's formatNumber)
  const formatNumber = useCallback((number: number | string | undefined | null): string => {
    if (number === undefined || number === null) return '0'
    
    let value: number
    if (typeof number === 'string') {
      value = parseFloat(number) || 0
    } else {
      value = number
    }
    
    // Use English locale formatting with thousands separators
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
  }, [])
  
  // Format shift date (matching Flutter's formatShiftDate)
  const formatShiftDate = useCallback((dateString: string): string => {
    try {
      const date = new Date(dateString)
      const formatter = new Intl.DateTimeFormat('ar-SA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      return formatter.format(date)
    } catch (e) {
      return dateString
    }
  }, [])
  
  // Apply date filter
  const applyDateFilter = useCallback((startDate: Date, endDate: Date | null) => {
    setSelectedDateRange({ startDate, endDate })
  }, [])
  
  // Clear date filter
  const clearDateFilter = useCallback(() => {
    setSelectedDateRange(null)
  }, [])
  
  // Get employee name
  const employeeName = useMemo(() => {
    if (profileData?.employeeName) {
      return profileData.employeeName
    }
    return user?.name || t('employee_service') || 'Employee'
  }, [profileData, user, t])
  
  // Get recent shifts (last 10, matching Flutter's getRecentShifts)
  const getRecentShifts = useCallback((): ShiftReport[] => {
    if (!profileData || isCS || !('shifts' in profileData)) {
      return []
    }
    
    const shifts = (profileData as ProfileData).shifts || []
    if (shifts.length === 0) return []
    
    // Sort by start date (most recent first) and take first 10
    const sortedShifts = [...shifts].sort((a, b) => {
      const dateA = new Date(a.startedAt).getTime()
      const dateB = new Date(b.startedAt).getTime()
      return dateB - dateA
    })
    
    return sortedShifts.slice(0, 10)
  }, [profileData, isCS])
  
  // Data getters
  const totalSales = useMemo(() => profileData?.totalSales || '0', [profileData])
  const totalOrders = useMemo(() => profileData?.totalOrders || 0, [profileData])
  const totalProducts = useMemo(() => profileData?.totalProducts || 0, [profileData])
  
  const cashSales = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.cashSales || '0'
    }
    return (profileData as ProfileData)?.cashSales || '0'
  }, [profileData, isCS])
  
  const cashOrdersCount = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.cashOrdersCount || 0
    }
    return (profileData as ProfileData)?.cashOrdersCount || 0
  }, [profileData, isCS])
  
  const networkSales = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.onlineSales || '0'
    }
    return (profileData as ProfileData)?.networkSales || '0'
  }, [profileData, isCS])
  
  const networkOrdersCount = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.onlineOrdersCount || 0
    }
    return (profileData as ProfileData)?.networkOrdersCount || 0
  }, [profileData, isCS])
  
  const partialSales = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.walletSales || '0'
    }
    return (profileData as ProfileData)?.partialSales || '0'
  }, [profileData, isCS])
  
  const partialOrdersCount = useMemo(() => {
    if (isCS) {
      return (profileData as CSProfileData)?.walletOrdersCount || 0
    }
    return (profileData as ProfileData)?.partialOrdersCount || 0
  }, [profileData, isCS])
  
  const bestSellingProducts = useMemo(() => {
    return profileData?.bestSellingProducts || []
  }, [profileData])
  
  const shifts = useMemo(() => {
    if (isCS || !profileData || !('shifts' in profileData)) {
      return []
    }
    return (profileData as ProfileData).shifts || []
  }, [profileData, isCS])
  
  // Refresh profile
  const refreshProfile = useCallback(() => {
    refetch()
  }, [refetch])
  
  return {
    // State
    profileData,
    isLoading,
    error,
    
    // Derived values
    isCS,
    
    // Date filter
    selectedDateRange,
    applyDateFilter,
    clearDateFilter,
    
    // Formatting functions
    formatNumber,
    formatShiftDate,
    
    // Data getters
    employeeName,
    totalSales,
    totalOrders,
    totalProducts,
    cashSales,
    cashOrdersCount,
    networkSales,
    networkOrdersCount,
    partialSales,
    partialOrdersCount,
    bestSellingProducts,
    shifts,
    getRecentShifts,
    
    // Actions
    refreshProfile,
  }
}

